import React, { useEffect, useState } from 'react';
import {
    Card, CardBody, Col, Container, Row, Table, Spinner, Badge, Input, Label
} from 'reactstrap';
import { toast } from 'react-toastify';
import api from '../../api';
import useAuth from './useAuth';
import { DashboardPageHeader, EmptyState } from '../../Components/Common/DashboardKit';

// ─────────────────────────────────────────────────────────────────────────────
// G-37 — "My FTO Balance" employee self-view.
//
// Direct replacement for the legacy frmFtoViewYTD form. Shows the eight
// balance fields the legacy form displayed, plus a list of consumed FTO
// entries for the year.
//
// Endpoints (both pre-existing):
//   GET /api/fto/balances?empId={me}&year={y}  → FtoBalancesDto
//   GET /api/fto/consumed?empId={me}&year={y}  → list of consumed entries
//
// EmpID comes from JWT via useAuth — employee cannot see anyone else's.
// ─────────────────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, tone = 'secondary', big = false }) => (
    <Card className="h-100" style={big ? { borderColor: 'var(--vz-primary)' } : undefined}>
        <CardBody className="text-center">
            <p className="text-muted small mb-1">{label}</p>
            <h3 className={`mb-0 text-${tone}`}>
                {typeof value === 'number' ? value.toFixed(2) : (value ?? '—')}
            </h3>
        </CardBody>
    </Card>
);

const MyFtoBalance = () => {
    const auth = useAuth();
    const [year, setYear]         = useState(new Date().getFullYear());
    const [bal, setBal]           = useState(null);
    const [entries, setEntries]   = useState([]);
    const [loading, setLoading]   = useState(false);

    const load = async (y) => {
        if (!auth?.empId) return;
        setLoading(true);
        try {
            const [b, c] = await Promise.all([
                api.get('fto/balances', { params: { empId: auth.empId, year: y } }),
                api.get('fto/consumed', { params: { empId: auth.empId, year: y } }),
            ]);
            setBal(b?.data || null);
            setEntries(Array.isArray(c?.data) ? c.data : []);
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Failed to load FTO balance.');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(year); /* eslint-disable-next-line */ }, [auth?.empId]);

    // Derived totals (legacy formula):
    //   NetAvailable = Base - Used   (Base already includes Comp + Carried)
    //   BalanceAfterScheduled = NetAvailable - Scheduled
    const net = bal
        // Round 7 (G-49 fix): baseFtoEarned already includes comp+carried; do NOT re-add.
        ? bal.baseFtoEarned - bal.ftoDaysUsed
        : null;
    const afterSched = bal ? net - bal.scheduledForFuture : null;

    return (
        <div className="page-content">
        <Container fluid>
            <DashboardPageHeader
                icon="ri-calendar-check-line"
                eyebrow="Attendance"
                title="My FTO Balance"
                subtitle={`Year-to-date FTO summary for ${auth?.empId || '—'}.`}
                tone="blue"
                breadcrumb={[
                    { label: "Dashboards", to: "/" },
                    { label: "Attendance", to: "/workflows?wf=pac" },
                    { label: "My FTO Balance" },
                ]}
                actions={
                    <div className="d-flex align-items-center gap-2">
                        <Label className="small text-white mb-0">Year</Label>
                        <Input
                            type="number" bsSize="sm" style={{ width: 110 }}
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
                            onBlur={(e) => load(parseInt(e.target.value, 10) || year)}
                        />
                    </div>
                }
            />

            {loading && !bal ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : !bal ? (
                <Card><CardBody>
                    <EmptyState icon="ri-calendar-check-line" title="No data"
                        message={`No FTO balance data for ${year}.`} />
                </CardBody></Card>
            ) : (
                <>
                    {/* Top row — the four "earned vs spent" buckets */}
                    <Row className="mb-3 g-3">
                        <Col xs={6} md={3}><StatCard label="Base earned this year"   value={bal.baseFtoEarned}        tone="primary" /></Col>
                        <Col xs={6} md={3}><StatCard label="Days used"               value={bal.ftoDaysUsed}          tone="danger"  /></Col>
                        <Col xs={6} md={3}><StatCard label="Compensation days"       value={bal.compensationDays}     tone="success" /></Col>
                        <Col xs={6} md={3}><StatCard label="Carried from last year"  value={bal.carriedFromPrevious}  tone="info"    /></Col>
                    </Row>

                    {/* Middle row — scheduled / shutdown */}
                    <Row className="mb-3 g-3">
                        <Col xs={6} md={6}><StatCard label="Scheduled for future"      value={bal.scheduledForFuture}      tone="warning" /></Col>
                        <Col xs={6} md={6}><StatCard label="Remaining shutdown days"   value={bal.remainingShutdownDays}   tone="secondary" /></Col>
                    </Row>

                    {/* Bottom row — the two summary totals (highlighted) */}
                    <Row className="mb-4 g-3">
                        <Col xs={6} md={6}><StatCard label="Net available (Base − Used)" value={net} tone={net >= 0 ? 'success' : 'danger'} big /></Col>
                        <Col xs={6} md={6}><StatCard label="Balance after scheduled" value={afterSched} tone={afterSched >= 0 ? 'success' : 'danger'} big /></Col>
                    </Row>

                    {/* Consumed-entries table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="text-muted mb-0">Consumed FTO this year</h6>
                                <span className="text-muted small">
                                    {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                                </span>
                            </div>

                            {entries.length === 0 ? (
                                <p className="text-muted small mb-0">No FTO consumed in {year}.</p>
                            ) : (
                                <Table size="sm" className="mb-0" responsive>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th className="text-end">Days</th>
                                            <th className="text-end">Hours paid</th>
                                            <th>Type</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map(r => (
                                            <tr key={r.id}>
                                                <td>{new Date(r.dateConsumed).toLocaleDateString()}</td>
                                                <td className="text-end">{r.daysConsumed.toFixed(2)}</td>
                                                <td className="text-end">{r.ftoHoursToPay.toFixed(2)}</td>
                                                <td>{r.business
                                                        ? <Badge color="info">Business</Badge>
                                                        : <Badge color="secondary">Personal</Badge>}</td>
                                                <td className="small">{r.reason || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </Container>
        </div>
    );
};

export default MyFtoBalance;
